import React, { useState } from "react";
import { useSimulator } from "@/context/SimulatorContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { NumberField } from "@/components/simulator/NumberField";
import { fmtEUR, fmtNum, fmtPct } from "@/lib/format";
import { ENERGY_SCENARIOS } from "@/lib/defaults";
import { toast } from "sonner";
import {
  Sun,
  Zap,
  Battery,
  Atom,
  Flame,
  Gauge,
  TrendingUp,
  Plus,
  Trash2,
  AlertTriangle,
  Info,
  Waves,
  Trees,
  Warehouse,
  Sprout,
} from "lucide-react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Cell,
  ComposedChart,
  Line,
  Legend,
  Area,
} from "recharts";

const MONTH_LABELS = ["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"];
const SUB_ICONS = { bacino: Waves, serra: Sprout, terra: Trees, tettoie: Warehouse };
const SUB_COLORS = { bacino: "#38bdf8", serra: "#10b981", terra: "#f59e0b", tettoie: "#8b5cf6" };

const CLASS_STYLES = {
  VALIDATED: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  LITERATURE: "bg-sky-500/15 text-sky-300 border-sky-500/40",
  ASSUMPTION: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  USER_INPUT: "bg-slate-500/15 text-slate-300 border-slate-500/40",
  TO_VALIDATE: "bg-rose-500/15 text-rose-300 border-rose-500/40",
};

const ClassTag = ({ value }) => {
  if (!value) return null;
  const label = value.replace("_", " ");
  return (
    <Badge className={`text-[9px] font-semibold px-1.5 py-0 border ${CLASS_STYLES[value] || CLASS_STYLES.ASSUMPTION}`}>
      {label}
    </Badge>
  );
};

export const EnergyTab = () => {
  const {
    inputs, summary,
    updateEnergy, updateSubsystem, updateBattery, updateElectrolyzer, updateFuelCell,
    loadEnergyScenario, updateLoadCategory, addLoadCategory, removeLoadCategory,
  } = useSimulator();

  const e = inputs.energy || {};
  const en = summary.energy || {};
  const [newLoadName, setNewLoadName] = useState("");
  const [newLoadKwh, setNewLoadKwh] = useState(5000);

  const subs = e.subsystems || {};
  const monthly = en.monthly || [];

  const monthlyChart = monthly.map((m, i) => ({
    name: MONTH_LABELS[i],
    FV: Math.round(m.production),
    Consumo: Math.round(m.consumption),
    "Auto+Batt": Math.round(m.direct + m.fromBattery),
    Elettro: Math.round(m.toElectro),
    Rete: Math.round(m.fromGrid),
    FC: Math.round(m.fcElectric),
    H2Storage: Math.round(m.h2StorageEnd),
  }));

  return (
    <div className="space-y-6">
      {/* HEADER */}
      <div className="flex items-start gap-4 flex-wrap">
        <div className="rounded-xl bg-amber-500/10 border border-amber-500/30 p-3">
          <Sun className="h-7 w-7 text-amber-400" />
        </div>
        <div className="flex-1 min-w-[300px]">
          <h2 className="font-display text-2xl font-bold text-slate-100">
            H2 Farm — Energy Balance
          </h2>
          <p className="text-sm text-slate-400 mt-1 max-w-3xl">
            Modello energetico integrato con 4 sottosistemi FV, bilancio mensile, priorità dispatch
            (autoconsumo → batteria → elettrolizzatore → rete), storage H₂, fuel cell invernale e
            recupero calore. Tutti i valori sono classificati come <ClassTag value="ASSUMPTION" />
            {" o "}<ClassTag value="TO_VALIDATE" /> salvo diversa indicazione.
          </p>
        </div>
      </div>

      {/* MODEL CHANGE SUMMARY */}
      <Card data-testid="energy-change-summary" className="bg-gradient-to-br from-rose-500/10 via-amber-500/10 to-emerald-500/10 border border-amber-500/40 p-5">
        <div className="flex items-start gap-3 flex-wrap">
          <AlertTriangle className="h-5 w-5 text-amber-400 mt-0.5 shrink-0" />
          <div className="flex-1 min-w-[280px]">
            <div className="text-xs font-semibold uppercase tracking-widest text-amber-300">
              Model change — Correzione stima FV
            </div>
            <div className="mt-2 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
              <div className="border border-slate-700/60 rounded-lg p-3 bg-slate-950/50">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                  Previous optimistic estimate
                </div>
                <div className="mt-1 font-mono-tab text-rose-300 line-through">
                  {fmtNum(e.previous_kwp || 1132, 0)} kWp
                </div>
                <div className="text-xs text-slate-400 font-mono-tab line-through">
                  ≈ {fmtNum((e.previous_gwh || 1.867), 2)} GWh/anno
                </div>
              </div>
              <div className="border border-emerald-500/30 rounded-lg p-3 bg-emerald-500/5">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-emerald-300">
                  Nuova baseline (Base scenario)
                </div>
                <div className="mt-1 font-mono-tab text-emerald-300 font-bold">
                  {fmtNum(en.totalKwp || 0, 0)} kWp
                </div>
                <div className="text-xs text-slate-300 font-mono-tab">
                  ≈ {fmtNum((en.totalProduction || 0) / 1000000, 2)} GWh/anno
                </div>
              </div>
              <div className="border border-slate-700/60 rounded-lg p-3 bg-slate-950/50">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                  Differenza
                </div>
                <div className="mt-1 font-mono-tab text-slate-200">
                  −{fmtNum((e.previous_kwp || 1132) - (en.totalKwp || 0), 0)} kWp
                </div>
                <div className="text-xs text-slate-400 font-mono-tab">
                  −{fmtNum(((e.previous_gwh || 1.867) - (en.totalProduction || 0) / 1000000), 2)} GWh
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>

      {/* Energy Scenarios */}
      <Card className="bg-slate-900/70 border-slate-800 p-5">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-display text-lg font-semibold text-slate-100">
            Scenari FV (indipendenti dai lotti produttivi)
          </h3>
          <span className="text-xs font-mono-tab text-slate-500">
            attivo: <span className="text-emerald-300 font-semibold">{e.active_scenario || "custom"}</span>
          </span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {Object.entries(ENERGY_SCENARIOS).map(([key, sc]) => {
            const active = e.active_scenario === key;
            return (
              <button
                key={key}
                data-testid={`energy-scenario-${key}`}
                onClick={() => {
                  loadEnergyScenario(key);
                  toast.success(`Scenario energetico "${sc.name}" caricato`);
                }}
                className={`text-left p-4 rounded-lg border transition-all ${
                  active
                    ? "border-emerald-500/60 bg-emerald-500/10 shadow-[0_0_15px_rgba(16,185,129,0.15)]"
                    : "border-slate-800 bg-slate-950/50 hover:border-slate-700"
                }`}
              >
                <div className="font-display font-semibold text-slate-100">{sc.name}</div>
                <div className="text-[11px] text-slate-500 mt-1 font-mono-tab">Target: {sc.target_kwp} kWp</div>
              </button>
            );
          })}
        </div>
      </Card>

      {/* Top KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3">
        <KpiTile testId="kpi-total-kwp" label="FV Installato" value={`${fmtNum(en.totalKwp || 0, 0)} kWp`} accent="amber" icon={Sun} />
        <KpiTile testId="kpi-production" label="Produzione FV" value={`${fmtNum((en.totalProduction || 0) / 1000, 0)} MWh`} accent="amber" icon={Zap} />
        <KpiTile testId="kpi-consumption" label="Consumo MVP" value={`${fmtNum((en.totalConsumption || 0) / 1000, 0)} MWh`} accent="rose" icon={Gauge} />
        <KpiTile testId="kpi-autoconsumo" label="Autoconsumo FV" value={fmtPct(en.autoconsumoFV || 0, 1)} accent="emerald" icon={TrendingUp} />
        <KpiTile testId="kpi-autosufficienza" label="Autosufficienza" value={fmtPct(en.autosufficienza || 0, 1)} accent="emerald" icon={Battery} />
        <KpiTile testId="kpi-h2-year" label="H₂ prodotto" value={`${fmtNum(en.totalH2Produced || 0, 0)} kg`} accent="violet" icon={Atom} />
      </div>

      {/* PV SUBSYSTEMS */}
      <Card className="bg-slate-900/70 border-slate-800 p-6">
        <h3 className="font-display text-lg font-semibold text-slate-100 mb-4">
          Sottosistemi Fotovoltaici (4 sistemi)
        </h3>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {Object.entries(subs).map(([key, s]) => {
            const Icon = SUB_ICONS[key] || Sun;
            const color = SUB_COLORS[key] || "#f59e0b";
            const sub = (en.subsystems || []).find((x) => x.key === key) || {};
            return (
              <div
                key={key}
                data-testid={`pv-subsystem-${key}`}
                className={`border rounded-lg p-4 space-y-3 ${
                  s.enabled ? "border-slate-700 bg-slate-950/50" : "border-slate-800 bg-slate-950/30 opacity-60"
                }`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Icon className="h-4 w-4 shrink-0" style={{ color }} />
                    <div className="font-display font-semibold text-slate-100 truncate">{s.name}</div>
                    <ClassTag value={s.classification} />
                  </div>
                  <Switch
                    data-testid={`pv-subsystem-${key}-enabled`}
                    checked={s.enabled}
                    onCheckedChange={(v) => updateSubsystem(key, { enabled: v })}
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {key === "bacino" && (
                    <>
                      <MiniInput label="Superficie bacino" testId={`pv-${key}-surface`} value={s.surface_m2} unit="m²" onChange={(v) => updateSubsystem(key, { surface_m2: v })} />
                      <MiniInput label="Copertura FV" testId={`pv-${key}-coverage`} value={s.coverage_pct} unit="%" onChange={(v) => updateSubsystem(key, { coverage_pct: v })} />
                    </>
                  )}
                  {key === "serra" && (
                    <>
                      <MiniInput label="Superficie serra" testId={`pv-${key}-surface`} value={s.surface_m2} unit="m²" onChange={(v) => updateSubsystem(key, { surface_m2: v })} />
                      <MiniInput label="Trasparenza" testId={`pv-${key}-transparency`} value={s.transparency_pct} unit="%" onChange={(v) => updateSubsystem(key, { transparency_pct: v })} />
                    </>
                  )}
                  {key === "terra" && (
                    <>
                      <MiniInput label="Terreno disponibile" testId={`pv-${key}-terrain`} value={s.terrain_m2} unit="m²" onChange={(v) => updateSubsystem(key, { terrain_m2: v })} />
                      <MiniInput label="% Occupazione" testId={`pv-${key}-occupation`} value={s.occupation_pct} unit="%" onChange={(v) => updateSubsystem(key, { occupation_pct: v })} />
                      <MiniInput label="Inclinazione" testId={`pv-${key}-tilt`} value={s.tilt} unit="°" onChange={(v) => updateSubsystem(key, { tilt: v })} />
                      <div className="col-span-2 flex items-center justify-between border border-slate-800 rounded-md px-2.5 py-1.5">
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Bifacciale (+{s.bifacial_gain_pct || 0}%)</span>
                        <Switch data-testid={`pv-${key}-bifacial`} checked={s.bifacial} onCheckedChange={(v) => updateSubsystem(key, { bifacial: v })} />
                      </div>
                    </>
                  )}
                  {key === "tettoie" && (
                    <>
                      <MiniInput label="Superficie" testId={`pv-${key}-surface`} value={s.surface_m2} unit="m²" onChange={(v) => updateSubsystem(key, { surface_m2: v })} />
                      <div />
                    </>
                  )}
                  <MiniInput label="Potenza specifica" testId={`pv-${key}-wpm2`} value={s.wp_per_m2} unit="Wp/m²" onChange={(v) => updateSubsystem(key, { wp_per_m2: v })} />
                  <MiniInput label="Produzione specifica" testId={`pv-${key}-specprod`} value={s.specific_prod} unit="kWh/kWp" onChange={(v) => updateSubsystem(key, { specific_prod: v })} />
                </div>

                <div className="pt-2 border-t border-slate-800 flex items-center justify-between text-xs">
                  <span className="text-slate-400">Potenza calcolata</span>
                  <span className="font-mono-tab font-bold" style={{ color }}>
                    {fmtNum(sub.kwp || 0, 1)} kWp · {fmtNum((sub.production || 0) / 1000, 1)} MWh
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-5 pt-4 border-t border-slate-800 flex items-center justify-between flex-wrap gap-2">
          <div className="text-xs text-slate-400">
            <ClassTag value="ASSUMPTION" /> <span className="ml-2">Produzione specifica 1.650 kWh/kWp — Maremma/Grosseto</span>
          </div>
          <div className="font-mono-tab text-emerald-300 font-bold text-lg">
            TOTALE {fmtNum(en.totalKwp || 0, 0)} kWp · {fmtNum((en.totalProduction || 0) / 1000, 0)} MWh/anno
          </div>
        </div>
      </Card>

      {/* MONTHLY BALANCE */}
      <Card className="bg-slate-900/70 border-slate-800 p-6">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="font-display text-lg font-semibold text-slate-100">
            Bilancio Mensile — Produzione, Consumo, Dispatch
          </h3>
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Info className="h-3 w-3" />
            <span>Struttura predisposta per calcolo orario (8760 h) — MVP mensile</span>
          </div>
        </div>
        <div data-testid="energy-monthly-chart" className="h-[320px]">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={monthlyChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1f2937" />
              <XAxis dataKey="name" stroke="#64748b" tick={{ fontSize: 11, fill: "#94a3b8" }} />
              <YAxis
                stroke="#64748b"
                tick={{ fontSize: 10, fill: "#94a3b8" }}
                tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
              />
              <Tooltip
                formatter={(v) => `${fmtNum(v)} kWh`}
                contentStyle={{ background: "#0b0f19", border: "1px solid #1f2937", borderRadius: 8 }}
              />
              <Legend wrapperStyle={{ fontSize: 11, color: "#94a3b8" }} />
              <Bar dataKey="FV" fill="#f59e0b" radius={[3, 3, 0, 0]} />
              <Bar dataKey="Elettro" fill="#8b5cf6" radius={[3, 3, 0, 0]} />
              <Bar dataKey="Rete" fill="#f43f5e" radius={[3, 3, 0, 0]} />
              <Line type="monotone" dataKey="Consumo" stroke="#38bdf8" strokeWidth={2.5} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="Auto+Batt" stroke="#10b981" strokeWidth={2} dot={{ r: 2 }} />
              <Line type="monotone" dataKey="FC" stroke="#ef4444" strokeWidth={2} dot={{ r: 2 }} strokeDasharray="4 3" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <div className="mt-3 text-[11px] text-slate-500">
          <ClassTag value="ASSUMPTION" /> Profilo mensile FV Grosseto · Consumo distribuito uniforme (predisposto per profilo orario)
        </div>
      </Card>

      {/* LOAD CATEGORIES */}
      <Card className="bg-slate-900/70 border-slate-800 p-6">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="font-display text-lg font-semibold text-slate-100">
            Consumi Elettrici per Sottosistema (11 categorie MVP)
          </h3>
          <div className="text-xs text-slate-400 flex items-center gap-2">
            <ClassTag value="ASSUMPTION" />
            <span>Range MVP: 400-650 MWh/anno · Consumo attuale <b className="text-slate-200 font-mono-tab">{fmtNum((en.totalConsumption || 0) / 1000, 0)} MWh</b></span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          {(e.load_categories || []).map((c) => (
            <div
              key={c.id}
              data-testid={`load-cat-${c.id}`}
              className="flex items-center gap-2 border border-slate-800 rounded-lg p-2.5 bg-slate-950/40"
            >
              <div className="flex-1 min-w-0">
                <Input
                  data-testid={`load-cat-${c.id}-name`}
                  value={c.name}
                  onChange={(ev) => updateLoadCategory(c.id, { name: ev.target.value })}
                  className="h-7 bg-transparent border-slate-800 text-slate-200 text-xs"
                />
                <div className="mt-1 flex items-center gap-2">
                  <ClassTag value={c.classification} />
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Input
                  data-testid={`load-cat-${c.id}-kwh`}
                  type="number"
                  value={c.kwh_year}
                  onChange={(ev) => updateLoadCategory(c.id, { kwh_year: Number(ev.target.value) || 0 })}
                  className="h-8 w-24 bg-slate-950 border-slate-800 text-right font-mono-tab text-xs"
                />
                <span className="text-[10px] text-slate-500 font-mono-tab">kWh/y</span>
                <Button
                  data-testid={`load-cat-${c.id}-delete`}
                  size="sm"
                  variant="ghost"
                  onClick={() => removeLoadCategory(c.id)}
                  className="h-7 w-7 p-0 text-rose-400 hover:bg-rose-500/10"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-3 pt-3 border-t border-slate-800 flex items-stretch gap-2">
          <Input
            data-testid="load-new-name"
            value={newLoadName}
            onChange={(ev) => setNewLoadName(ev.target.value)}
            placeholder="Nuova categoria..."
            className="flex-1 bg-slate-950 border-slate-800 h-8 text-xs"
          />
          <Input
            type="number"
            value={newLoadKwh}
            onChange={(ev) => setNewLoadKwh(Number(ev.target.value) || 0)}
            className="w-24 bg-slate-950 border-slate-800 h-8 text-right font-mono-tab text-xs"
          />
          <Button
            data-testid="load-add-button"
            onClick={() => {
              if (!newLoadName.trim()) { toast.error("Inserisci un nome"); return; }
              addLoadCategory({ name: newLoadName.trim(), kwh_year: newLoadKwh });
              setNewLoadName("");
              setNewLoadKwh(5000);
              toast.success(`Consumo "${newLoadName}" aggiunto`);
            }}
            className="bg-amber-500 hover:bg-amber-600 text-slate-950 h-8 text-xs"
          >
            <Plus className="h-3 w-3 mr-1" />Add
          </Button>
        </div>
      </Card>

      {/* PRIORITY DISPATCH + BATTERY + ELECTROLYZER + FUEL CELL */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card className="bg-slate-900/70 border-emerald-500/20 border p-5 space-y-4">
          <div className="flex items-center gap-2 text-emerald-400">
            <Battery className="h-4 w-4" />
            <h3 className="font-display text-base font-semibold">Batteria (BESS)</h3>
            <ClassTag value={e.battery?.classification} />
          </div>
          <NumberField label="Capacità" value={e.battery?.capacity_kwh || 0} unit="kWh" min={0} max={5000} step={50} testId="battery-capacity" sliderTheme="algae-slider" onChange={(v) => updateBattery({ capacity_kwh: v })} />
          <NumberField label="Efficienza round-trip" value={e.battery?.round_trip_efficiency_pct || 0} unit="%" min={70} max={98} step={0.5} testId="battery-rte" sliderTheme="algae-slider" onChange={(v) => updateBattery({ round_trip_efficiency_pct: v })} />
          <NumberField label="Depth of Discharge" value={e.battery?.dod_pct || 0} unit="%" min={50} max={100} step={1} testId="battery-dod" sliderTheme="algae-slider" onChange={(v) => updateBattery({ dod_pct: v })} />
          <NumberField label="CAPEX unitario" value={e.battery?.capex_eur_kwh || 0} unit="€/kWh" min={100} max={1200} step={25} testId="battery-capex" sliderTheme="violet-slider" onChange={(v) => updateBattery({ capex_eur_kwh: v })} />
          <div className="pt-2 border-t border-slate-800 text-xs flex justify-between">
            <span className="text-slate-400">CAPEX totale</span>
            <span className="font-mono-tab text-violet-300 font-semibold">{fmtEUR(en.bess_capex || 0)}</span>
          </div>
        </Card>

        <Card className="bg-slate-900/70 border-violet-500/20 border p-5 space-y-4">
          <div className="flex items-center gap-2 text-violet-400">
            <Atom className="h-4 w-4" />
            <h3 className="font-display text-base font-semibold">Elettrolizzatore + Storage H₂</h3>
            <ClassTag value={e.electrolyzer?.classification} />
          </div>
          <NumberField label="Potenza" value={e.electrolyzer?.power_kw || 0} unit="kW" min={0} max={2000} step={10} testId="electro-power" sliderTheme="violet-slider" onChange={(v) => updateElectrolyzer({ power_kw: v })} />
          <NumberField label="Efficienza" value={e.electrolyzer?.kwh_per_kg_h2 || 0} unit="kWh/kg H₂" min={40} max={80} step={0.5} testId="electro-efficiency" sliderTheme="violet-slider" hint="PEM 50-55 · Alcalino 55-65" onChange={(v) => updateElectrolyzer({ kwh_per_kg_h2: v })} />
          <NumberField label="Ore operative/anno" value={e.electrolyzer?.operating_hours_year || 0} unit="h" min={500} max={8000} step={100} testId="electro-hours" sliderTheme="violet-slider" onChange={(v) => updateElectrolyzer({ operating_hours_year: v })} />
          <NumberField label="Storage H₂" value={e.electrolyzer?.storage_capacity_kg || 0} unit="kg" min={0} max={5000} step={10} testId="electro-storage" sliderTheme="violet-slider" onChange={(v) => updateElectrolyzer({ storage_capacity_kg: v })} />
          <NumberField label="Pressione stoccaggio" value={e.electrolyzer?.storage_pressure_bar || 0} unit="bar" min={30} max={700} step={10} testId="electro-pressure" sliderTheme="violet-slider" onChange={(v) => updateElectrolyzer({ storage_pressure_bar: v })} />
          <NumberField label="% surplus dedicato" value={e.electrolyzer?.surplus_share_pct || 0} unit="%" min={0} max={100} step={5} testId="electro-share" sliderTheme="violet-slider" onChange={(v) => updateElectrolyzer({ surplus_share_pct: v })} />
          <div className="pt-2 border-t border-slate-800 text-xs space-y-1">
            <div className="flex justify-between"><span className="text-slate-400">CAPEX elettro.</span><span className="font-mono-tab text-violet-300">{fmtEUR(en.electro_capex || 0)}</span></div>
            <div className="flex justify-between"><span className="text-slate-400">CAPEX storage H₂</span><span className="font-mono-tab text-violet-300">{fmtEUR(en.h2_storage_capex || 0)}</span></div>
          </div>
        </Card>

        <Card className="bg-slate-900/70 border-rose-500/20 border p-5 space-y-4">
          <div className="flex items-center gap-2 text-rose-400">
            <Flame className="h-4 w-4" />
            <h3 className="font-display text-base font-semibold">Fuel Cell (Inverno)</h3>
            <ClassTag value={e.fuel_cell?.classification} />
          </div>
          <NumberField label="Potenza" value={e.fuel_cell?.power_kw || 0} unit="kW" min={0} max={1000} step={5} testId="fc-power" sliderTheme="saffron-slider" onChange={(v) => updateFuelCell({ power_kw: v })} />
          <NumberField label="Efficienza elettrica" value={e.fuel_cell?.electric_efficiency_pct || 0} unit="%" min={30} max={65} step={0.5} testId="fc-eff" sliderTheme="saffron-slider" onChange={(v) => updateFuelCell({ electric_efficiency_pct: v })} />
          <NumberField label="Recupero calore" value={e.fuel_cell?.heat_recovery_pct || 0} unit="%" min={0} max={70} step={1} testId="fc-heat" sliderTheme="saffron-slider" onChange={(v) => updateFuelCell({ heat_recovery_pct: v })} />
          <NumberField label="Temp. calore utile" value={e.fuel_cell?.useful_heat_temp_c || 0} unit="°C" min={40} max={90} step={1} testId="fc-temp" sliderTheme="saffron-slider" onChange={(v) => updateFuelCell({ useful_heat_temp_c: v })} />
          <NumberField label="Ore operative/anno" value={e.fuel_cell?.operating_hours_year || 0} unit="h" min={500} max={4000} step={100} testId="fc-hours" sliderTheme="saffron-slider" onChange={(v) => updateFuelCell({ operating_hours_year: v })} />
          <div className="pt-2 border-t border-slate-800 text-xs space-y-1">
            <div className="flex justify-between"><span className="text-slate-400">Elettr. da FC</span><span className="font-mono-tab text-rose-300">{fmtNum((en.totalFCElectric || 0) / 1000, 1)} MWh</span></div>
            <div className="flex justify-between"><span className="text-slate-400">Calore recuperato</span><span className="font-mono-tab text-amber-300">{fmtNum(en.heat_recovered_mwh || 0, 1)} MWh th</span></div>
            <div className="flex justify-between"><span className="text-slate-400">CAPEX FC</span><span className="font-mono-tab text-violet-300">{fmtEUR(en.fc_capex || 0)}</span></div>
          </div>
        </Card>
      </div>

      {/* ENERGY BALANCE DASHBOARD */}
      <Card data-testid="energy-balance-dashboard" className="bg-slate-900/70 border-slate-800 p-6">
        <h3 className="font-display text-lg font-semibold text-slate-100 mb-4">
          H2 Farm Energy Balance — Dashboard
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
          <BalanceRow label="FV prodotto" value={`${fmtNum((en.totalProduction || 0) / 1000, 1)} MWh`} color="text-amber-300" />
          <BalanceRow label="Consumo totale" value={`${fmtNum((en.totalConsumption || 0) / 1000, 1)} MWh`} color="text-rose-300" />
          <BalanceRow label="Autoconsumo diretto" value={`${fmtNum((en.totalDirect || 0) / 1000, 1)} MWh`} color="text-emerald-300" />
          <BalanceRow label="Da batteria" value={`${fmtNum((en.totalFromBattery || 0) / 1000, 1)} MWh`} color="text-sky-300" />
          <BalanceRow label="All'elettrolizzatore" value={`${fmtNum((en.totalToElectro || 0) / 1000, 1)} MWh`} color="text-violet-300" />
          <BalanceRow label="Acquistata rete" value={`${fmtNum((en.totalFromGrid || 0) / 1000, 1)} MWh`} color="text-rose-400" />
          <BalanceRow label="Esportata rete" value={`${fmtNum((en.totalToGrid || 0) / 1000, 1)} MWh`} color="text-slate-300" />
          <BalanceRow label="Curtailment" value={`${fmtNum((en.totalCurtailment || 0) / 1000, 1)} MWh`} color="text-slate-500" />
          <BalanceRow label="H₂ prodotto" value={`${fmtNum(en.totalH2Produced || 0, 0)} kg`} color="text-violet-300" />
          <BalanceRow label="H₂ utilizzato FC" value={`${fmtNum(en.totalH2ConsumedFC || 0, 0)} kg`} color="text-rose-300" />
          <BalanceRow label="Fuel Cell elettr." value={`${fmtNum((en.totalFCElectric || 0) / 1000, 1)} MWh`} color="text-sky-300" />
          <BalanceRow label="Calore recuperato" value={`${fmtNum(en.heat_recovered_mwh || 0, 1)} MWhth`} color="text-amber-300" />
          <BalanceRow label="Autoconsumo FV" value={fmtPct(en.autoconsumoFV || 0, 1)} color="text-emerald-300" />
          <BalanceRow label="Autosufficienza" value={fmtPct(en.autosufficienza || 0, 1)} color="text-emerald-400" />
          <BalanceRow label="Risparmio energetico" value={fmtEUR(en.energy_savings || 0)} color="text-emerald-300" />
          <BalanceRow label="Costo rete residuo" value={fmtEUR(en.grid_cost || 0)} color="text-rose-300" />
        </div>
      </Card>

      {/* IMPATTO OPEX */}
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card data-testid="opex-no-system" className="bg-slate-900/70 border-rose-500/20 border p-5">
          <div className="text-xs font-semibold uppercase tracking-widest text-rose-300">Senza sistema energetico</div>
          <div className="mt-2 font-display font-extrabold text-2xl text-rose-300 font-mono-tab">
            {fmtEUR((en.totalConsumption || 0) * (e.grid_price_eur_kwh || 0))}
          </div>
          <div className="text-xs text-slate-500 mt-1">costo energia annuo pieno da rete</div>
        </Card>
        <Card data-testid="opex-with-system" className="bg-slate-900/70 border-emerald-500/20 border p-5">
          <div className="text-xs font-semibold uppercase tracking-widest text-emerald-300">Con FV + H₂ + Storage</div>
          <div className="mt-2 font-display font-extrabold text-2xl text-emerald-300 font-mono-tab">
            {fmtEUR(en.grid_cost || 0)}
          </div>
          <div className="text-xs text-slate-500 mt-1">costo residuo di rete dopo autoconsumo</div>
        </Card>
        <Card data-testid="energy-payback" className="bg-gradient-to-br from-emerald-500/15 via-amber-500/10 to-violet-500/15 border border-emerald-500/40 p-5">
          <div className="text-xs font-semibold uppercase tracking-widest text-emerald-300">Payback asset energetico</div>
          <div className="mt-2 font-display font-extrabold text-2xl text-slate-100 font-mono-tab">
            {en.energy_payback_years !== null && en.energy_payback_years !== undefined
              ? `${en.energy_payback_years.toFixed(1)} anni`
              : "N/D"}
          </div>
          <div className="text-xs text-slate-400 mt-1">
            CAPEX {fmtEUR(en.energy_capex || 0)} · Risparmio {fmtEUR(en.energy_savings || 0)}/anno
          </div>
        </Card>
      </div>

      {/* PROJECTION SETTINGS */}
      <Card className="bg-slate-900/70 border-slate-800 p-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-sky-500/10 border border-sky-500/30 p-2">
              <TrendingUp className="h-4 w-4 text-sky-400" />
            </div>
            <div>
              <div className="font-display font-semibold text-slate-100">Integrazione con proiezione 5Y</div>
              <div className="text-xs text-slate-500">
                Il CAPEX viene applicato all'anno di installazione, il risparmio dagli anni successivi.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="w-40">
              <NumberField
                label="Anno installazione"
                value={e.install_year || 1}
                onChange={(v) => updateEnergy({ install_year: v })}
                min={1}
                max={5}
                step={1}
                unit=""
                testId="energy-install-year"
                sliderTheme="violet-slider"
              />
            </div>
            <ToggleRow
              testId="energy-apply-opex"
              label="Applica risparmio a OPEX"
              value={e.apply_opex_savings}
              onChange={(v) => updateEnergy({ apply_opex_savings: v })}
            />
            <ToggleRow
              testId="energy-include-capex"
              label="Includi CAPEX in totale"
              value={e.include_in_capex}
              onChange={(v) => updateEnergy({ include_in_capex: v })}
            />
          </div>
        </div>
      </Card>
    </div>
  );
};

const KpiTile = ({ label, value, subtext, accent, icon: Icon, testId }) => {
  const border = { amber: "border-amber-500/30", emerald: "border-emerald-500/30", rose: "border-rose-500/30", violet: "border-violet-500/30" }[accent];
  const color = { amber: "text-amber-300", emerald: "text-emerald-300", rose: "text-rose-300", violet: "text-violet-300" }[accent];
  return (
    <Card data-testid={testId} className={`bg-slate-900/70 border ${border} p-3`}>
      <div className="flex items-center gap-1.5 text-[9px] font-semibold uppercase tracking-widest text-slate-400">
        {Icon && <Icon className="h-3 w-3" />}
        <span className="truncate">{label}</span>
      </div>
      <div className={`mt-1.5 font-display font-extrabold text-lg font-mono-tab ${color}`}>{value}</div>
      {subtext && <div className="mt-0.5 text-[9px] text-slate-500 font-mono-tab">{subtext}</div>}
    </Card>
  );
};

const MiniInput = ({ label, value, unit, onChange, testId }) => (
  <div>
    <Label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</Label>
    <div className="mt-1 flex items-center gap-1">
      <Input
        data-testid={testId}
        type="number"
        value={value}
        onChange={(ev) => onChange(Number(ev.target.value) || 0)}
        className="h-8 bg-slate-950 border-slate-800 text-right font-mono-tab text-sm"
      />
      <span className="text-[10px] text-slate-500 min-w-[38px]">{unit}</span>
    </div>
  </div>
);

const BalanceRow = ({ label, value, color }) => (
  <div className="border border-slate-800 rounded-md p-2.5 bg-slate-950/40">
    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
    <div className={`mt-0.5 font-mono-tab font-semibold text-sm ${color}`}>{value}</div>
  </div>
);

const ToggleRow = ({ label, value, onChange, testId }) => (
  <div className="flex items-center gap-2">
    <span className="text-xs font-semibold text-slate-300">{label}</span>
    <Switch data-testid={testId} checked={value} onCheckedChange={onChange} />
  </div>
);
